import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import getSupabaseBrowserClient from "../../hooks/use-supabase-browser";
import {
  useQuery,
  useUpdateMutation,
} from "@supabase-cache-helpers/postgrest-react-query";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNotifications } from "../../providers/notifications";
import Page from "../../components/Page";
import { Tables } from "../../../utils/database.types";
import { getValet } from "../../api/get-valet";
import { getVenues } from "../../api/get-venues";

const EditVenuePage: React.FC = () => {
  const navigate = useNavigate();
  const { valetId } = useParams() as { valetId: string };
  const supabase = getSupabaseBrowserClient();

  const {
    data: valet,
    isLoading,
    isError,
  } = useQuery(getValet(supabase, valetId));
  const {
    register,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm<Tables<"users">>({ mode: "onChange" });
  const { data: venues = [] } = useQuery(getVenues(supabase));
  const { notify } = useNotifications();

  useEffect(() => {
    if (valet) {
      reset({
        id: valet.id,
        full_name: valet.full_name,
        scheduler_ext_id: valet.scheduler_ext_id,
        venue_id: valet.venue_id,
      });
    }
  }, [valet, reset]);

  const { mutateAsync } = useUpdateMutation(
    supabase.from("users"),
    ["id"],
    "id",
    {
      onSuccess: () => {
        notify("Valet updated successfully!", "success", 3000);
        navigate("/valets");
      },
      onError: (error) => {
        console.error("Error while updating the Venue:", error);
        notify(
          "Error while updating the venue. Please try again.",
          "error",
          3000
        );
      },
    }
  );

  if (isLoading) {
    return <span className="loading loading-dots loading-lg"></span>;
  }

  if (isError || !valet) {
    return <div>Error loading venue data.</div>;
  }

  const onSubmit: SubmitHandler<Tables<"users">> = async (data) => {
    await mutateAsync(data);
  };

  return (
    <Page title={`Edit ${valet.full_name}`}>
      <div>
        <div className="card bg-base-100 shadow-sm">
          <form onSubmit={handleSubmit(onSubmit)} className="card-body">
            <div className="form-control">
              <label className="label">
                <span className="label-text">Full Name</span>
              </label>
              <input
                type="text"
                {...register("full_name", { required: true })}
                className="input input-bordered"
              />
            </div>
            <div className="form-control">
              <label className="label">
                <span
                  className="label-text tooltip tooltip-right"
                  data-tip="The ID of the Valet in the external system (ie. GetSling). This is used to link the venue to the external system."
                >
                  External ID ⓘ
                </span>
              </label>
              <input
                type="text"
                id="scheduler_ext_id"
                {...register("scheduler_ext_id")}
                className="input input-bordered"
              />
            </div>
            <div className="form-control">
              <label className="label">
                <span
                  className="label-text tooltip tooltip-right"
                  data-tip="Valets in order to operate need to be assigned to a Venue. This happens either manually or through an external scheduling system such as GetSling."
                >
                  Current assigned to the Venue ⓘ
                </span>
              </label>
              {venues && (
                <select
                  className="select select-bordered w-full"
                  {...register("venue_id", {
                    setValueAs: (value) => (value === "" ? null : value),
                  })}
                >
                  <option key={"unassign"} value={""}>
                    -- NONE --
                  </option>
                  {venues.map((venue) => (
                    <option key={venue.id} value={venue.id}>
                      {venue.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="flex justify-between" style={{ marginTop: "10px" }}>
              <button
                type="submit"
                className="btn btn-outline"
                onClick={() => navigate("/valets")}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-success"
                disabled={!isValid}
              >
                Save Changes
              </button>
            </div>
          </form>
        </div>
      </div>
    </Page>
  );
};

export default EditVenuePage;
